import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useParent } from '../../contexts/parentContext';
import ProfileImage from '../../components/profileImage';
import DeleteConfirmationDialog from '../../components/common/DeleteConfirmationDialog';
import api from '../../services/api';

const Children = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { selectChild, exitControlMode } = useParent();
  const [activeTab, setActiveTab] = useState('children');
  const [children, setChildren] = useState([]);
  const [sentRequests, setSentRequests] = useState([]);
  const [receivedRequests, setReceivedRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchUsername, setSearchUsername] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [selectedRoles, setSelectedRoles] = useState(['full']);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, child: null, type: null });

  useEffect(() => {
    // Exit control mode when entering children page
    exitControlMode();
    fetchData();
  }, []);

  const handleChildSelect = (child) => {
    selectChild(child);
    navigate('/parent/child-control/full');
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const [childrenRes, sentRes, receivedRes] = await Promise.all([
        api.get('/parent/get-all-players-of-parent'),
        api.get('/parent/get-all-sent-requests-to-add-parent'),
        api.get('/parent/get-all-received-requests-to-add-parent')
      ]);

      setChildren(childrenRes.data?.data || []);
      setSentRequests(sentRes.data?.data || []);
      setReceivedRequests(receivedRes.data?.data || []);
    } catch (err) {
      setError(t('parent.failedToLoadData'));
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchUsername.trim()) return;
    
    try {
      const response = await api.get(`/parent/search-for-parent-or-child-by-username?username=${searchUsername}`);
      setSearchResult(response.data?.data || null);
    } catch (err) {
      setError(t('parent.userNotFound'));
      setSearchResult(null);
    }
  };

  const handleRoleChange = (role) => {
    if (role === 'full') {
      // If selecting full, clear other roles
      setSelectedRoles(['full']);
    } else {
      // If selecting specific role, remove 'full' and toggle the role
      const newRoles = selectedRoles.filter(r => r !== 'full');
      if (newRoles.includes(role)) {
        const filteredRoles = newRoles.filter(r => r !== role);
        setSelectedRoles(filteredRoles.length > 0 ? filteredRoles : ['full']);
      } else {
        setSelectedRoles([...newRoles, role]);
      }
    }
  };

  const sendRequest = async (playerUsername) => {
    try {
      await api.post('/parent/send-request-from-parent-to-add-player', {
        playerUsername,
        role: selectedRoles
      });
      
      fetchData();
      setSearchResult(null);
      setSearchUsername('');
      setSelectedRoles(['full']); // Reset to default
    } catch (err) {
      setError(t('parent.failedToSendRequest'));
    }
  };

  const cancelRequest = async (requestId) => {
    try {
      await api.delete('/parent/delete-request-from-parent-to-add-player', {
        data: { requestId }
      });
      fetchData();
    } catch (err) {
      setError(t('parent.failedToCancelRequest'));
    }
  };

  const handleRequest = async (requestId, status) => {
    try {
      await api.put('/parent/approve-or-reject-request-to-add-parent', {
        requestId,
        status
      });
      fetchData();
    } catch (err) {
      setError(t('parent.failedToHandleRequest'));
    }
  };

  const removeChild = async () => {
    if (!deleteDialog.child) return;
    
    try {
      await api.delete('/parent/delete-parent-from-user', {
        data: { deletedUserId: deleteDialog.child._id }
      });
      
      fetchData();
      setDeleteDialog({ open: false, child: null, type: null });
    } catch (err) {
      setError(t('parent.failedToRemoveChild'));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600 font-medium">{t('parent.loadingChildren')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        {/* Header */}
        <div className="bg-gradient-to-r from-orange-600 to-red-600 rounded-2xl shadow-xl overflow-hidden mb-8" style={{background: 'linear-gradient(to right, #E25820, #B8441A)'}}>
          <div className="p-8 text-white">
            <h1 className="text-4xl font-bold mb-2">{t('parent.childrenManagement')}</h1>
            <p className="text-orange-100 text-lg">{t('parent.manageChildren')}</p>
          </div>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {/* Tabs Navigation */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 mb-8">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6">
              {['children', 'received', 'sent', 'search'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab
                      ? 'text-orange-600 border-orange-500'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                  style={activeTab === tab ? {borderBottomColor: '#E25820', color: '#E25820'} : {}}
                >
                  {t(`parent.${tab}`)}
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">
            {/* Children Tab */}
            {activeTab === 'children' && (
              <div>
                <h3 className="text-xl font-semibold mb-4">{t('parent.myChildren')}</h3>
                {children.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg className="w-8 h-8 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3z" />
                      </svg>
                    </div>
                    <h4 className="text-lg font-medium text-gray-900 mb-2">{t('parent.noChildren')}</h4>
                    <p className="text-gray-500">{t('parent.noChildrenDescription')}</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {children.map((child) => (
                      <div key={child.userId} className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow">
                        <div className="flex items-center space-x-4 mb-4">
                          <ProfileImage
                            image={child.profilePicture}
                            alt={child.userName}
                            className="w-16 h-16 rounded-full object-cover"
                          />
                          <div className="flex-1">
                            <h4 className="font-semibold text-gray-900">
                              @{child.userName}
                            </h4>
                            <p className="text-sm text-gray-500">{child.accountType}</p>
                            <div className="flex flex-wrap gap-1 mt-2">
                              {child.role.map((role) => (
                                <span key={role} className="px-2 py-1 bg-orange-100 text-orange-800 rounded text-xs">
                                  {role}
                                </span>
                              ))}
                            </div>
                            <p className="text-xs text-gray-400 mt-1">
                              Added {child.timeAgo}
                            </p>
                          </div>
                        </div>
                        
                        <button
                          onClick={() => handleChildSelect(child)}
                          className="block w-full px-3 py-2 text-white rounded-lg transition-colors text-center text-sm"
                          style={{backgroundColor: '#E25820'}}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#B8441A'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = '#E25820'}
                        >
                          {t('parent.controlChild')}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Received Requests Tab */}
            {activeTab === 'received' && (
              <div>
                <h3 className="text-xl font-semibold mb-4">{t('parent.receivedRequests')}</h3>
                {receivedRequests.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg className="w-8 h-8 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M8 9a3 3 0 100-6 3 3 0 000 6zM8 11a6 6 0 016 6H2a6 6 0 016-6zM16 7a1 1 0 10-2 0v1h-1a1 1 0 100 2h1v1a1 1 0 102 0v-1h1a1 1 0 100-2h-1V7z" />
                      </svg>
                    </div>
                    <h4 className="text-lg font-medium text-gray-900 mb-2">{t('parent.noReceivedRequests')}</h4>
                    <p className="text-gray-500">{t('parent.noReceivedRequestsDescription')}</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {receivedRequests.map((request) => (
                      <div key={request.requestId} className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-4">
                            <ProfileImage
                              image={request.profilePicture}
                              alt={request.userName}
                              className="w-12 h-12 rounded-full object-cover"
                            />
                            <div>
                              <h4 className="font-semibold text-gray-900">
                                @{request.userName}
                              </h4>
                              <p className="text-sm text-gray-500">{request.accountType}</p>
                              <p className="text-xs text-gray-400">
                                {request.timeAgo}
                              </p>
                              <div className="flex flex-wrap gap-1 mt-1">
                                {request.role.map((role) => (
                                  <span key={role} className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                                    {t(`parent.${role}Access`)}
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleRequest(request.requestId, 'approved')}
                              className="px-4 py-2 text-white rounded-lg transition-colors text-sm"
                              style={{backgroundColor: '#E25820'}}
                              onMouseEnter={(e) => e.target.style.backgroundColor = '#B8441A'}
                              onMouseLeave={(e) => e.target.style.backgroundColor = '#E25820'}
                            >
                              {t('parent.accept')}
                            </button>
                            <button
                              onClick={() => handleRequest(request.requestId, 'rejected')}
                              className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors text-sm"
                            >
                              {t('parent.reject')}
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Sent Requests Tab */}
            {activeTab === 'sent' && (
              <div>
                <h3 className="text-xl font-semibold mb-4">{t('parent.sentRequests')}</h3>
                {sentRequests.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg className="w-8 h-8 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M8 9a3 3 0 100-6 3 3 0 000 6zM8 11a6 6 0 016 6H2a6 6 0 016-6zM16 7a1 1 0 10-2 0v1h-1a1 1 0 100 2h1v1a1 1 0 102 0v-1h1a1 1 0 100-2h-1V7z" />
                      </svg>
                    </div>
                    <h4 className="text-lg font-medium text-gray-900 mb-2">{t('parent.noSentRequests')}</h4>
                    <p className="text-gray-500">{t('parent.noSentRequestsDescription')}</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {sentRequests.map((request) => (
                      <div key={request.requestId} className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-4">
                            <ProfileImage
                              image={request.profilePicture}
                              alt={request.userName}
                              className="w-12 h-12 rounded-full object-cover"
                            />
                            <div>
                              <h4 className="font-semibold text-gray-900">
                                @{request.userName}
                              </h4>
                              <p className="text-sm text-gray-500">{request.accountType}</p>
                              <p className="text-xs text-gray-400">
                                {request.timeAgo}
                              </p>
                              <div className="flex flex-wrap gap-1 mt-1">
                                {request.role.map((role) => (
                                  <span key={role} className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs">
                                    {t(`parent.${role}Access`)}
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>
                          <button
                            onClick={() => cancelRequest(request.requestId)}
                            className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors text-sm"
                          >
                            {t('parent.cancel')}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Search Tab */}
            {activeTab === 'search' && (
              <div>
                <h3 className="text-xl font-semibold mb-4">{t('parent.searchForChild')}</h3>
                
                {/* Search Input */}
                <div className="bg-gray-50 p-6 rounded-lg mb-6">
                  <div className="flex space-x-4">
                    <input
                      type="text"
                      value={searchUsername}
                      onChange={(e) => setSearchUsername(e.target.value)}
                      placeholder={t('parent.enterChildUsername')}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
                      style={{'--tw-ring-color': '#E25820'}}
                      onFocus={(e) => e.target.style.borderColor = '#E25820'}
                      onBlur={(e) => e.target.style.borderColor = '#D1D5DB'}
                    />
                    <button
                      onClick={handleSearch}
                      className="px-6 py-2 text-white rounded-lg transition-colors"
                      style={{backgroundColor: '#E25820'}}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#B8441A'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = '#E25820'}
                    >
                      {t('parent.search')}
                    </button>
                  </div>
                </div>

                {/* Role Selection */}
                <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
                  <h4 className="text-lg font-semibold mb-4">{t('parent.selectPermissions')}</h4>
                  <p className="text-gray-600 text-sm mb-4">{t('parent.selectPermissionsDescription')}</p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {/* Full Access */}
                    <div 
                      onClick={() => handleRoleChange('full')}
                      className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                        selectedRoles.includes('full') 
                          ? 'bg-orange-50' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      style={selectedRoles.includes('full') ? {borderColor: '#E25820'} : {}}
                    >
                      <div className="flex items-center mb-2">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mr-3 ${
                          selectedRoles.includes('full') ? 'border-gray-300' : 'border-gray-300'
                        }`}
                        style={selectedRoles.includes('full') ? {borderColor: '#E25820', backgroundColor: '#E25820'} : {}}>
                          {selectedRoles.includes('full') && (
                            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          )}
                        </div>
                        <h5 className="font-semibold text-gray-900">{t('parent.fullAccess')}</h5>
                      </div>
                      <p className="text-sm text-gray-600">{t('parent.fullAccessDescription')}</p>
                    </div>

                    {/* Feed Access */}
                    <div 
                      onClick={() => handleRoleChange('feed')}
                      className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                        selectedRoles.includes('feed') 
                          ? 'bg-orange-50' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      style={selectedRoles.includes('feed') ? {borderColor: '#E25820'} : {}}
                    >
                      <div className="flex items-center mb-2">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mr-3 ${
                          selectedRoles.includes('feed') ? 'border-gray-300' : 'border-gray-300'
                        }`}
                        style={selectedRoles.includes('feed') ? {borderColor: '#E25820', backgroundColor: '#E25820'} : {}}>
                          {selectedRoles.includes('feed') && (
                            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          )}
                        </div>
                        <h5 className="font-semibold text-gray-900">{t('parent.feedAccess')}</h5>
                      </div>
                      <p className="text-sm text-gray-600">{t('parent.feedAccessDescription')}</p>
                    </div>

                    {/* Event Access */}
                    <div 
                      onClick={() => handleRoleChange('event')}
                      className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                        selectedRoles.includes('event') 
                          ? 'bg-orange-50' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      style={selectedRoles.includes('event') ? {borderColor: '#E25820'} : {}}
                    >
                      <div className="flex items-center mb-2">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mr-3 ${
                          selectedRoles.includes('event') ? 'border-gray-300' : 'border-gray-300'
                        }`}
                        style={selectedRoles.includes('event') ? {borderColor: '#E25820', backgroundColor: '#E25820'} : {}}>
                          {selectedRoles.includes('event') && (
                            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          )}
                        </div>
                        <h5 className="font-semibold text-gray-900">{t('parent.eventAccess')}</h5>
                      </div>
                      <p className="text-sm text-gray-600">{t('parent.eventAccessDescription')}</p>
                    </div>

                    {/* Performance Access */}
                    <div 
                      onClick={() => handleRoleChange('performance')}
                      className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                        selectedRoles.includes('performance') 
                          ? 'bg-orange-50' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      style={selectedRoles.includes('performance') ? {borderColor: '#E25820'} : {}}
                    >
                      <div className="flex items-center mb-2">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mr-3 ${
                          selectedRoles.includes('performance') ? 'border-gray-300' : 'border-gray-300'
                        }`}
                        style={selectedRoles.includes('performance') ? {borderColor: '#E25820', backgroundColor: '#E25820'} : {}}>
                          {selectedRoles.includes('performance') && (
                            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          )}
                        </div>
                        <h5 className="font-semibold text-gray-900">{t('parent.performanceAccess')}</h5>
                      </div>
                      <p className="text-sm text-gray-600">{t('parent.performanceAccessDescription')}</p>
                    </div>

                    {/* Payment Access */}
                    <div 
                      onClick={() => handleRoleChange('payment')}
                      className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                        selectedRoles.includes('payment') 
                          ? 'bg-orange-50' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                      style={selectedRoles.includes('payment') ? {borderColor: '#E25820'} : {}}
                    >
                      <div className="flex items-center mb-2">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center mr-3 ${
                          selectedRoles.includes('payment') ? 'border-gray-300' : 'border-gray-300'
                        }`}
                        style={selectedRoles.includes('payment') ? {borderColor: '#E25820', backgroundColor: '#E25820'} : {}}>
                          {selectedRoles.includes('payment') && (
                            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          )}
                        </div>
                        <h5 className="font-semibold text-gray-900">{t('parent.paymentAccess')}</h5>
                      </div>
                      <p className="text-sm text-gray-600">{t('parent.paymentAccessDescription')}</p>
                    </div>
                  </div>

                  {/* Selected Roles Summary */}
                  <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                    <h5 className="font-semibold text-gray-900 mb-2">{t('parent.selectedPermissions')}:</h5>
                    <div className="flex flex-wrap gap-2">
                      {selectedRoles.map((role) => (
                        <span key={role} className="px-3 py-1 rounded-full text-sm text-white" style={{backgroundColor: '#E25820'}}>
                          {t(`parent.${role}Access`)}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {searchResult && (
                  <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <ProfileImage
                          image={searchResult.profilePicture}
                          alt={searchResult.userName}
                          className="w-16 h-16 rounded-full object-cover"
                        />
                        <div>
                          <h4 className="font-semibold text-gray-900">
                            @{searchResult.userName}
                          </h4>
                          <p className="text-sm text-gray-500">{searchResult.accountType}</p>
                          <p className="text-xs text-gray-400">
                            {t('parent.joined')}: {new Date(searchResult.joinedAt).toLocaleDateString()}
                          </p>
                          {searchResult.relationshipExists && (
                            <p className="text-xs text-blue-600">
                              {t('parent.relationshipExists')}: {searchResult.relationshipType}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col space-y-2">
                        {searchResult.canSendRequest && !searchResult.relationshipExists && (
                          <button
                            onClick={() => sendRequest(searchResult.userName)}
                            className="px-6 py-2 text-white rounded-lg transition-colors text-sm"
                            style={{backgroundColor: '#E25820'}}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#B8441A'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = '#E25820'}
                          >
                            {t('parent.sendRequest')}
                          </button>
                        )}
                        {searchResult.relationshipExists && (
                          <span className="px-6 py-2 bg-gray-100 text-gray-600 rounded-lg text-sm text-center">
                            {t('parent.alreadyConnected')}
                          </span>
                        )}
                        {!searchResult.canSendRequest && !searchResult.relationshipExists && (
                          <span className="px-6 py-2 bg-yellow-100 text-yellow-600 rounded-lg text-sm text-center">
                            {t('parent.requestPending')}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, child: null, type: null })}
        onConfirm={removeChild}
        title={t('parent.confirmRemoveChild')}
        message={t('parent.confirmRemoveChildMessage')}
      />
    </div>
  );
};

export default Children; 